/**
 * Shared constants for company data clear (WebSocket lock + Operations UI).
 */

/** Must match backend DATA_CLEAR_TASK_TITLE. */
export const DATA_CLEAR_TASK_TITLE = "Company data clear";

/** Progress payload type from ProgressReportingTask data. */
export const DATA_CLEAR_TASK_TYPE = "company_data_clear";

export const DATA_CLEAR_STORAGE_KEY = "orderlink-data-clear";

/**
 * Keep Task Manager / lock suppress for Confirm→POST while GET may still
 * show the previous succeeded job.
 *
 * @const {number}
 */
export const DATA_CLEAR_START_GRACE_MS = 20_000;

/**
 * Per-company storage key so clears do not leak across companies in one browser.
 *
 * @param {string|number|undefined|null} companyId
 * @return {string}
 */
export const dataClearStorageKey = companyId =>
  companyId != null && companyId !== ""
    ? `${DATA_CLEAR_STORAGE_KEY}:${companyId}`
    : DATA_CLEAR_STORAGE_KEY;

/**
 * @param {string|number|undefined|null} companyId
 * @param {string|number|undefined|null} jobId
 * @return {string}
 */
export const dataClearNotifiedKey = (companyId, jobId) =>
  `${dataClearStorageKey(companyId)}:notified:${jobId || "none"}`;

/**
 * @param {string|number|undefined|null} companyId
 * @return {boolean}
 */
export const isClearMarkedInStorage = companyId => {
  const key = dataClearStorageKey(companyId);
  try {
    if (sessionStorage.getItem(key) === "running") return true;
    const shared = localStorage.getItem(key);
    return Boolean(shared && String(shared).startsWith("running"));
  } catch (_err) {
    return false;
  }
};

/**
 * Epoch ms from the localStorage running mark, or 0.
 *
 * @param {string|number|undefined|null} companyId
 * @return {number}
 */
export const dataClearMarkedAtMs = companyId => {
  try {
    const raw = localStorage.getItem(dataClearStorageKey(companyId));
    if (!raw || !String(raw).startsWith("running")) return 0;
    const parts = String(raw).split(":");
    const ts = Number(parts[parts.length - 1]);
    return Number.isFinite(ts) ? ts : 0;
  } catch (_err) {
    return 0;
  }
};

/**
 * Drop a stuck "running" mark when the server says the clear is idle.
 *
 * Keeps the mark during Confirm→POST grace so Task Manager does not flash back.
 *
 * @param {string|number|undefined|null} companyId
 * @param {string|undefined|null} jobStatus Latest GET/WS status, or null when none.
 * @param {number} [nowMs]
 * @return {boolean} True when this call cleared the mark.
 */
export const releaseStaleDataClearMark = (companyId, jobStatus, nowMs = Date.now()) => {
  if (jobStatus === "running") return false;
  if (!isClearMarkedInStorage(companyId) && !dataClearSuppressesTaskManager) {
    return false;
  }
  const markedAt = dataClearMarkedAtMs(companyId);
  if (markedAt && nowMs - markedAt < DATA_CLEAR_START_GRACE_MS) {
    return false;
  }
  persistDataClearIdle(companyId);
  return true;
};

/**
 * Synchronous flag so Task Manager can hide in the same tick as Clear starts.
 */
let dataClearSuppressesTaskManager = false;

/**
 * True while a clear is starting/running (storage or in-memory). Ignores the
 * short post-clear hold — that must not leave Task Manager hidden forever.
 *
 * @param {string|number|undefined|null} [companyId]
 * @return {boolean}
 */
export const isDataClearActivelyMarked = companyId =>
  dataClearSuppressesTaskManager || isClearMarkedInStorage(companyId);

/**
 * True while Task Manager should stay hidden, including the short post-clear
 * hold. Use {@link isDataClearActivelyMarked} for lock/progress UI.
 *
 * @param {string|number|undefined|null} [companyId]
 * @return {boolean}
 */
export const isDataClearSuppressingTaskManager = companyId =>
  isDataClearActivelyMarked(companyId) || Date.now() < taskManagerHoldUntilMs;

/** Keep Task Manager hidden briefly after unlock so it does not fade back in. */
let taskManagerHoldUntilMs = 0;

/** @const {number} */
const TASK_MANAGER_HOLD_AFTER_CLEAR_MS = 600;

/**
 * @param {boolean} active True while a company data clear is starting/running.
 */
export const setDataClearSuppressesTaskManager = active => {
  dataClearSuppressesTaskManager = Boolean(active);
  if (active) {
    taskManagerHoldUntilMs = 0;
  }
};

/**
 * Persist clear-running for this tab + sibling tabs (company-scoped).
 *
 * @param {string|number|undefined|null} companyId
 * @return {boolean} True when storage changed (caller may broadcast).
 */
export const persistDataClearRunning = companyId => {
  setDataClearSuppressesTaskManager(true);
  const key = dataClearStorageKey(companyId);
  try {
    const already =
      sessionStorage.getItem(key) === "running" ||
      String(localStorage.getItem(key) || "").startsWith("running");
    if (already) {
      return false;
    }
    sessionStorage.setItem(key, "running");
    localStorage.setItem(key, `running:${Date.now()}`);
    return true;
  } catch (_err) {
    // Private mode / quota — in-memory flag still works for this tab.
    return true;
  }
};

/**
 * Clear persisted clear-running for this company.
 *
 * @param {string|number|undefined|null} companyId
 */
export const persistDataClearIdle = companyId => {
  dataClearSuppressesTaskManager = false;
  taskManagerHoldUntilMs = Date.now() + TASK_MANAGER_HOLD_AFTER_CLEAR_MS;
  const key = dataClearStorageKey(companyId);
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
    sessionStorage.removeItem(DATA_CLEAR_STORAGE_KEY);
    localStorage.removeItem(DATA_CLEAR_STORAGE_KEY);
  } catch (_err) {
    // ignore
  }
};

/**
 * Whether this browser already showed a finish toast for this job.
 *
 * @param {string|number|undefined|null} companyId
 * @param {string|number|undefined|null} jobId
 * @return {boolean}
 */
export const hasDataClearNotified = (companyId, jobId) => {
  if (jobId == null || jobId === "") return false;
  try {
    return localStorage.getItem(dataClearNotifiedKey(companyId, jobId)) === "1";
  } catch (_err) {
    return false;
  }
};

/**
 * Mark finish toast as shown for this job (shared across tabs).
 *
 * @param {string|number|undefined|null} companyId
 * @param {string|number|undefined|null} jobId
 */
export const markDataClearNotified = (companyId, jobId) => {
  if (jobId == null || jobId === "") return;
  try {
    localStorage.setItem(dataClearNotifiedKey(companyId, jobId), "1");
  } catch (_err) {
    // ignore
  }
};

/**
 * RTK Query tags to invalidate after a successful full clear.
 *
 * @const {!Array<string>}
 */
export const DATA_CLEAR_INVALIDATE_TAGS = [
  "receiptList",
  "receiptDetail",
  "asnList",
  "consignmentList",
  "consignmentDetail",
  "filteredConsignmentList",
  "purchaseOrderList",
  "purchaseOrderDetail",
  "transferOrderList",
  "transferOrderDetail",
  "saleOrderList",
  "saleOrderDetail",
  "saleQuotationList",
  "quotation",
  "order",
  "Return",
  "billList",
  "billDetail",
  "PrePayment",
  "shipmentList",
  "picklistList",
  "Picklist",
  "stockLineList",
  "manufacturingOrderList",
  "moList",
  "cmoList",
  "coManufacturingOrderList",
  "productionRunList",
  "OperationsOverview",
  "PortalOrders",
];

/**
 * Session key: this tab observed a clear and must reload when it succeeds.
 *
 * @param {string|number|undefined|null} companyId
 * @return {string}
 */
export const dataClearPendingReloadKey = companyId =>
  `${dataClearStorageKey(companyId)}:pending-reload`;

/**
 * Mark that this tab should hard-reload after the clear succeeds.
 *
 * Survives React Strict Mode remounts (unlike in-memory prev-status refs).
 *
 * @param {string|number|undefined|null} companyId
 * @param {string|number|undefined|null} [jobId]
 * @return {void}
 */
export const markDataClearPendingReload = (companyId, jobId) => {
  try {
    sessionStorage.setItem(
      dataClearPendingReloadKey(companyId),
      jobId != null && jobId !== "" ? String(jobId) : "1",
    );
  } catch (_err) {
    // ignore
  }
};

/**
 * Whether this tab still owes a post-clear reload.
 *
 * @param {string|number|undefined|null} companyId
 * @return {boolean}
 */
export const hasDataClearPendingReload = companyId => {
  try {
    return Boolean(sessionStorage.getItem(dataClearPendingReloadKey(companyId)));
  } catch (_err) {
    return false;
  }
};

/**
 * Clear the pending-reload flag (after scheduling reload or on failed start).
 *
 * @param {string|number|undefined|null} companyId
 * @return {void}
 */
export const clearDataClearPendingReload = companyId => {
  try {
    sessionStorage.removeItem(dataClearPendingReloadKey(companyId));
  } catch (_err) {
    // ignore
  }
};

/**
 * Delay before hard reload after a successful clear (ms).
 *
 * Lets the lock mark clear and one paint settle, then reloads so every screen
 * drops stale RTK cache without invalidateTags flashing lists.
 *
 * @const {number}
 */
export const DATA_CLEAR_RELOAD_DELAY_MS = 400;

/**
 * Session key: show the success toast once after reload.
 *
 * @param {string|number|undefined|null} companyId
 * @return {string}
 */
export const dataClearReloadToastKey = companyId =>
  `${dataClearStorageKey(companyId)}:reload-toast`;

/**
 * Remember to toast after the post-clear window reload.
 *
 * @param {string|number|undefined|null} companyId
 * @return {void}
 */
export const markDataClearReloadToast = companyId => {
  try {
    sessionStorage.setItem(dataClearReloadToastKey(companyId), "1");
  } catch (_err) {
    // ignore
  }
};

/**
 * Consume the post-reload toast flag (one-shot).
 *
 * @param {string|number|undefined|null} companyId
 * @return {boolean}
 */
export const consumeDataClearReloadToast = companyId => {
  const key = dataClearReloadToastKey(companyId);
  try {
    if (sessionStorage.getItem(key) !== "1") return false;
    sessionStorage.removeItem(key);
    return true;
  } catch (_err) {
    return false;
  }
};

/**
 * Hard-reload the app after a successful clear so all screens drop wiped data.
 *
 * Replaces tag invalidation (which refetched mounted lists and flashed the UI).
 *
 * @return {void}
 */
export const scheduleDataClearWindowReload = () => {
  window.setTimeout(() => {
    window.location.reload();
  }, DATA_CLEAR_RELOAD_DELAY_MS);
};

/** @deprecated Prefer scheduleDataClearWindowReload after a successful clear. */
export const scheduleDataClearTagInvalidation = (
  dispatch,
  invalidateTagsAction,
) => {
  window.setTimeout(() => {
    dispatch(invalidateTagsAction([...DATA_CLEAR_INVALIDATE_TAGS]));
  }, DATA_CLEAR_RELOAD_DELAY_MS);
};

/** @deprecated Use scheduleDataClearWindowReload */
export const staggerDataClearTagInvalidation = scheduleDataClearTagInvalidation;

/** @deprecated Use DATA_CLEAR_RELOAD_DELAY_MS */
export const DATA_CLEAR_INVALIDATE_DELAY_MS = DATA_CLEAR_RELOAD_DELAY_MS;

/**
 * Whether a Task Manager task is the company data clear.
 *
 * @param {object|undefined} taskState Redux/SSE task state.
 * @return {boolean}
 */
export const isDataClearTask = taskState => {
  if (!taskState) return false;
  const type = taskState?.data?.type || taskState?.type;
  const title = taskState?.data?.title || taskState?.title;
  return type === DATA_CLEAR_TASK_TYPE || title === DATA_CLEAR_TASK_TITLE;
};

/**
 * Whether this task must stay out of the floating Task Manager.
 *
 * Only the company data clear is excluded — other is_visible=false jobs keep
 * their normal Task Manager behaviour.
 *
 * @param {object|undefined} taskState Redux/SSE task state.
 * @param {string} [runningClearTaskId] task_id from GET data_reset/current while running.
 * @return {boolean}
 */
export const isHiddenFromTaskManager = (taskState, runningClearTaskId = "") => {
  if (!taskState) return false;
  const id = taskState.task_id || "";
  if (runningClearTaskId && id && id === runningClearTaskId) return true;
  return isDataClearTask(taskState);
};

/**
 * Seconds-per-percent from phase checkpoints (EMA, recent phases weigh more).
 *
 * @param {Array<{progress: number, at: number}>} samples
 * @return {number|null}
 */
export const estimateSecPerPercent = samples => {
  if (!Array.isArray(samples) || samples.length < 2) return null;
  let ema = null;
  for (let i = 1; i < samples.length; i += 1) {
    const dp = Number(samples[i].progress) - Number(samples[i - 1].progress);
    const dt = Number(samples[i].at) - Number(samples[i - 1].at);
    if (!(dp > 0) || !(dt > 0)) continue;
    const rate = dt / dp;
    ema = ema == null ? rate : ema * 0.55 + rate * 0.45;
  }
  return ema;
};

/**
 * Append a progress checkpoint when percent advances.
 *
 * @param {Array<{progress: number, at: number}>} samples
 * @param {number} progress
 * @param {number} [atSec]
 * @return {Array<{progress: number, at: number}>}
 */
export const pushClearProgressSample = (samples, progress, atSec = Date.now() / 1000) => {
  const next = Array.isArray(samples) ? samples.slice() : [];
  const pct = Number(progress);
  if (!Number.isFinite(pct) || pct < 0) return next;
  const last = next[next.length - 1];
  if (last && last.progress === pct) return next;
  if (last && pct < last.progress) return [{ progress: pct, at: atSec }];
  next.push({ progress: pct, at: atSec });
  return next.length > 12 ? next.slice(next.length - 12) : next;
};

/**
 * Format remaining time from overall start + recent phase pace.
 *
 * Prefers how long the last few percent jumps took (phase boundaries), blended
 * with overall average so early estimates are not wild.
 *
 * @param {number} progress Percent 0-100.
 * @param {number|undefined} startedAt Unix seconds.
 * @param {Array<{progress: number, at: number}>} [samples]
 * @param {number} [nowSec] Injectable clock (tests).
 * @return {string}
 */
export const formatClearEta = (
  progress,
  startedAt,
  samples = [],
  nowSec = Date.now() / 1000,
) => {
  if (!progress || progress <= 0) return "Estimating time left…";
  if (progress >= 100) return "Finishing…";

  const now = Number(nowSec);
  const phaseRate = estimateSecPerPercent(samples);
  const overallRate =
    startedAt != null && Number(startedAt) > 0
      ? Math.max(0.05, now - Number(startedAt)) / Math.max(progress, 1)
      : null;

  let secPerPct = null;
  if (phaseRate != null && overallRate != null && samples.length >= 3) {
    secPerPct = phaseRate * 0.7 + overallRate * 0.3;
  } else if (phaseRate != null) {
    secPerPct = phaseRate;
  } else if (overallRate != null) {
    secPerPct = overallRate;
  }

  if (secPerPct == null) return "Working…";

  let remaining = secPerPct * (100 - progress);
  const last = samples[samples.length - 1];
  // Time already spent in the current phase counts partly toward the next jump.
  if (last && Number(last.progress) === Number(progress)) {
    const sinceLast = Math.max(0, now - Number(last.at));
    remaining = Math.max(8, remaining - sinceLast * 0.45);
  }

  if (remaining < 45) return "About a minute left";
  if (remaining < 90) return "About 1m left";
  const minutes = Math.max(1, Math.round(remaining / 60));
  return `About ${minutes}m left`;
};
